import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Shield, User, Mail, Lock, Eye, EyeOff, ClipboardList, 
  GraduationCap, Award, ChevronRight, Clock, CheckCircle2,
  AlertCircle, BookOpen, LogOut, RefreshCw, Coins, Trophy,
  Star, Zap, TrendingUp, History, Video
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Função para extrair URL de vídeo do texto (YouTube, Vimeo, etc.)
const extractVideoUrl = (text) => {
  if (!text) return null;
  
  // Regex patterns for video URLs
  const patterns = [
    // YouTube - various formats
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})(?:[^\s]*)?/i,
    // Vimeo
    /(?:https?:\/\/)?(?:www\.)?(?:vimeo\.com\/)(\d+)(?:[^\s]*)?/i,
    // Dailymotion
    /(?:https?:\/\/)?(?:www\.)?(?:dailymotion\.com\/video\/)([a-zA-Z0-9]+)(?:[^\s]*)?/i,
  ];
  
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      const fullUrl = match[0];
      
      // YouTube
      if (fullUrl.includes('youtube') || fullUrl.includes('youtu.be')) {
        const videoId = match[1];
        return {
          type: 'youtube',
          videoId,
          embedUrl: `https://www.youtube.com/embed/${videoId}`,
          originalUrl: fullUrl
        };
      }
      
      // Vimeo
      if (fullUrl.includes('vimeo')) {
        const videoId = match[1];
        return {
          type: 'vimeo',
          videoId,
          embedUrl: `https://player.vimeo.com/video/${videoId}`,
          originalUrl: fullUrl
        };
      }
      
      // Dailymotion
      if (fullUrl.includes('dailymotion')) {
        const videoId = match[1];
        return {
          type: 'dailymotion',
          videoId,
          embedUrl: `https://www.dailymotion.com/embed/video/${videoId}`,
          originalUrl: fullUrl
        };
      }
    }
  }
  
  return null;
};

// Componente para exibir vídeo responsivo
const VideoPlayer = ({ videoInfo }) => {
  if (!videoInfo) return null;
  
  return (
    <div className="mb-4">
      <div className="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-lg bg-slate-900">
        <iframe
          className="absolute top-0 left-0 w-full h-full"
          src={videoInfo.embedUrl}
          title="Video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
        <Video size={12} />
        Vídeo: {videoInfo.type.charAt(0).toUpperCase() + videoInfo.type.slice(1)}
      </p>
    </div>
  );
};

// Level colors and icons
const LEVEL_CONFIG = {
  'Bronze': { color: '#CD7F32', bgColor: 'bg-amber-100', textColor: 'text-amber-800', gradient: 'from-amber-600 to-amber-800' },
  'Prata': { color: '#C0C0C0', bgColor: 'bg-gray-100', textColor: 'text-gray-700', gradient: 'from-gray-400 to-gray-600' },
  'Ouro': { color: '#FFD700', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800', gradient: 'from-yellow-400 to-yellow-600' },
  'Diamante': { color: '#B9F2FF', bgColor: 'bg-cyan-100', textColor: 'text-cyan-800', gradient: 'from-cyan-400 to-blue-500' },
};

const EmployeePortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [pointsHistory, setPointsHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    // Check if already logged in via cookie (backend validates)
    // Try to fetch dashboard - if cookie is valid, it will work
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/employee-portal/dashboard`, {
          withCredentials: true  // Send cookies
        });
        setDashboardData(response.data);
        
        // Get user info from me endpoint
        const userResp = await axios.get(`${API_URL}/api/auth/me`, {
          withCredentials: true
        });
        if (userResp.data?.user) {
          setUser(userResp.data.user);
          setIsLoggedIn(true);
        }
      } catch (error) {
        // Not authenticated - show login form
        setIsLoggedIn(false);
      }
    };
    checkAuth();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/employee-portal/dashboard`, {
        withCredentials: true  // Send cookies
      });
      setDashboardData(response.data);
      
      // Check if must change password
      const userResp = await axios.get(`${API_URL}/api/users/me`, {
        withCredentials: true
      }).catch(() => null);
      
      if (userResp?.data?.must_change_password) {
        setShowChangePassword(true);
        toast.warning('Por favor, altere sua senha temporária');
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPointsHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/employee-portal/gamification/history`, {
        withCredentials: true
      });
      setPointsHistory(response.data);
      setShowHistory(true);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API_URL}/api/employee-portal/login`, {
        email: email.toLowerCase().trim(),
        password
      }, {
        withCredentials: true  // Receive and store cookie
      });
      
      const { user: userData } = response.data;
      
      // No longer storing token in localStorage - using httpOnly cookie
      setUser(userData);
      setIsLoggedIn(true);
      
      fetchDashboard();
      toast.success('Login realizado com sucesso!');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Credenciais inválidas');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('A nova senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    try {
      await axios.post(`${API_URL}/api/employee-portal/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        withCredentials: true
      });
      
      toast.success('Senha alterada com sucesso!');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    }
  };

  const handleLogout = async () => {
    try {
      // Call backend to clear httpOnly cookie
      await axios.post(`${API_URL}/api/employee-portal/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      // Continue with local cleanup
    }
    setIsLoggedIn(false);
    setUser(null);
    setDashboardData(null);
    setEmail('');
    setPassword('');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      nao_iniciado: { label: 'Não Iniciado', className: 'bg-gray-500' },
      em_andamento: { label: 'Em Andamento', className: 'bg-blue-500' },
      concluido: { label: 'Concluído', className: 'bg-emerald-500' },
      reprovado: { label: 'Reprovado', className: 'bg-red-500' }
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-500' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const getLevelConfig = (levelName) => {
    return LEVEL_CONFIG[levelName] || LEVEL_CONFIG['Bronze'];
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#1e3a5f] to-[#0d9488] rounded-xl mb-4">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#1e3a5f]">Portal do Funcionário</h1>
            <p className="text-gray-600 mt-1">CERP Brasil NR1</p>
          </div>
          
          <Card className="shadow-xl border-0">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-xl">Acesse sua conta</CardTitle>
              <CardDescription>
                Use o email e senha enviados para seu email
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="seu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                      data-testid="employee-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      required
                      data-testid="employee-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-[#1e3a5f] to-[#0d9488] hover:opacity-90"
                  disabled={loginLoading}
                  data-testid="employee-login-button"
                >
                  {loginLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <User className="w-4 h-4 mr-2" />
                  )}
                  Entrar
                </Button>
              </form>
            </CardContent>
          </Card>
          
          <p className="text-center text-sm text-gray-500 mt-4">
            Não tem acesso? Fale com o RH da sua empresa.
          </p>
        </div>
      </div>
    );
  }

  // Change Password Modal
  if (showChangePassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-2">
              <AlertCircle className="w-6 h-6 text-amber-600" />
            </div>
            <CardTitle>Alterar Senha</CardTitle>
            <CardDescription>
              Por segurança, altere sua senha temporária
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Senha Atual</label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Senha temporária recebida"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Nova Senha</label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Confirmar Nova Senha</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repita a nova senha"
                  required
                />
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowChangePassword(false)}
                  className="flex-1"
                >
                  Depois
                </Button>
                <Button type="submit" className="flex-1 bg-[#0d9488]">
                  Alterar Senha
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const gamification = dashboardData?.gamification || {};
  const levelInfo = dashboardData?.stats?.level || gamification?.level || {};
  const currentLevel = levelInfo?.current || { name: 'Bronze', icon: '🥉' };
  const nextLevel = levelInfo?.next;
  const levelConfig = getLevelConfig(currentLevel.name);

  // Main Portal
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50" data-testid="employee-portal">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#1e3a5f] to-[#0d9488] rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-[#1e3a5f]">Portal do Funcionário</h1>
              <p className="text-xs text-gray-500">CERP Brasil NR1</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Level Badge */}
            <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full ${levelConfig.bgColor}`}>
              <span className="text-lg">{currentLevel.icon}</span>
              <span className={`text-sm font-semibold ${levelConfig.textColor}`}>{currentLevel.name}</span>
            </div>
            
            <div className="text-right hidden sm:block">
              <p className="font-medium text-gray-900">{user?.nome}</p>
              <p className="text-xs text-gray-500">{dashboardData?.employee?.cargo}</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-gray-500 hover:text-red-500"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <nav className="flex gap-1">
            {[
              { id: 'dashboard', label: 'Início', icon: User },
              { id: 'gamification', label: 'Conquistas', icon: Trophy },
              { id: 'assessments', label: 'Avaliações', icon: ClipboardList },
              { id: 'trainings', label: 'Treinamentos', icon: GraduationCap },
              { id: 'certificates', label: 'Certificados', icon: Award }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#0d9488] text-[#0d9488]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-[#0d9488]" />
          </div>
        ) : (
          <>
            {/* Dashboard Tab */}
            {activeTab === 'dashboard' && (
              <div className="space-y-6">
                {/* Welcome Card with Level */}
                <Card className={`bg-gradient-to-r ${levelConfig.gradient} text-white border-0 overflow-hidden relative`}>
                  <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
                    <span className="text-8xl">{currentLevel.icon}</span>
                  </div>
                  <CardContent className="p-6 relative">
                    <div className="flex items-start justify-between">
                      <div>
                        <h2 className="text-2xl font-bold">
                          Olá, {dashboardData?.employee?.nome?.split(' ')[0]}! 👋
                        </h2>
                        <p className="text-white/80 mt-1">
                          {dashboardData?.employee?.cargo} • {dashboardData?.employee?.setor}
                        </p>
                        <div className="flex items-center gap-2 mt-3">
                          <Badge className="bg-white/20 text-white border-0">
                            Nível {currentLevel.name} {currentLevel.icon}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 text-white/90">
                          <Coins className="w-5 h-5" />
                          <span className="text-2xl font-bold">{dashboardData?.stats?.coins || 0}</span>
                        </div>
                        <p className="text-xs text-white/70">moedas</p>
                      </div>
                    </div>
                    
                    {/* Level Progress */}
                    {nextLevel && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-white/80 mb-1">
                          <span>{dashboardData?.stats?.total_points || 0} pts</span>
                          <span>Próximo: {nextLevel.name} ({nextLevel.min_points} pts)</span>
                        </div>
                        <Progress 
                          value={levelInfo?.progress || 0} 
                          className="h-2 bg-white/20"
                        />
                        <p className="text-xs text-white/70 mt-1">
                          Faltam {levelInfo?.points_to_next || 0} pontos para o próximo nível
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 text-center">
                      <div className="w-12 h-12 mx-auto bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center mb-2">
                        <Star className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-2xl font-bold text-[#1e3a5f]">{dashboardData?.stats?.total_points || 0}</p>
                      <p className="text-xs text-gray-500">Pontos Totais</p>
                    </CardContent>
                  </Card>
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 text-center">
                      <div className="w-12 h-12 mx-auto bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center mb-2">
                        <Coins className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-2xl font-bold text-[#1e3a5f]">{dashboardData?.stats?.coins || 0}</p>
                      <p className="text-xs text-gray-500">Moedas</p>
                    </CardContent>
                  </Card>
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 text-center">
                      <div className="w-12 h-12 mx-auto bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center mb-2">
                        <ClipboardList className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-2xl font-bold text-[#1e3a5f]">{dashboardData?.stats?.pending_assessments_count || 0}</p>
                      <p className="text-xs text-gray-500">Avaliações Pendentes</p>
                    </CardContent>
                  </Card>
                  <Card className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4 text-center">
                      <div className="w-12 h-12 mx-auto bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-xl flex items-center justify-center mb-2">
                        <Award className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-2xl font-bold text-[#1e3a5f]">{dashboardData?.stats?.trainings_completed || 0}</p>
                      <p className="text-xs text-gray-500">Certificados</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Pending Assessments Alert */}
                {dashboardData?.pending_assessments?.length > 0 && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-amber-800 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" />
                        Avaliações Pendentes
                        <Badge className="bg-amber-600 ml-2">+50 pts cada</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {dashboardData.pending_assessments.slice(0, 3).map(assessment => (
                          <div 
                            key={assessment.id} 
                            className="flex items-center justify-between p-3 bg-white rounded-lg border"
                          >
                            <div>
                              <p className="font-medium">{assessment.titulo}</p>
                              {assessment.prazo && (
                                <p className="text-xs text-gray-500 flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> Prazo: {assessment.prazo}
                                </p>
                              )}
                            </div>
                            <Button 
                              size="sm"
                              className="bg-[#0d9488]"
                              onClick={() => window.open(`/avaliar/${assessment.token}`, '_blank')}
                            >
                              Responder <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Gamification Tab */}
            {activeTab === 'gamification' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-[#1e3a5f]">Suas Conquistas</h2>
                
                {/* Current Level Card */}
                <Card className={`bg-gradient-to-r ${levelConfig.gradient} text-white border-0`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center">
                        <span className="text-5xl">{currentLevel.icon}</span>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold">Nível {currentLevel.name}</h3>
                        <p className="text-white/80">{dashboardData?.stats?.total_points || 0} pontos acumulados</p>
                        {nextLevel && (
                          <div className="mt-2">
                            <Progress value={levelInfo?.progress || 0} className="h-3 bg-white/20" />
                            <p className="text-xs text-white/70 mt-1">
                              {levelInfo?.points_to_next || 0} pontos para {nextLevel.name}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Level Progression */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#0d9488]" />
                      Níveis de Progressão
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { name: 'Bronze', icon: '🥉', min: 0, color: 'from-amber-600 to-amber-800' },
                        { name: 'Prata', icon: '🥈', min: 500, color: 'from-gray-400 to-gray-600' },
                        { name: 'Ouro', icon: '🥇', min: 1500, color: 'from-yellow-400 to-yellow-600' },
                        { name: 'Diamante', icon: '💎', min: 5000, color: 'from-cyan-400 to-blue-500' },
                      ].map((level, idx) => {
                        const isActive = currentLevel.name === level.name;
                        const isUnlocked = (dashboardData?.stats?.total_points || 0) >= level.min;
                        return (
                          <div 
                            key={level.name}
                            className={`p-4 rounded-xl text-center transition-all ${
                              isActive 
                                ? `bg-gradient-to-br ${level.color} text-white shadow-lg scale-105` 
                                : isUnlocked
                                  ? 'bg-gray-100'
                                  : 'bg-gray-50 opacity-50'
                            }`}
                          >
                            <span className="text-4xl block mb-2">{level.icon}</span>
                            <p className={`font-bold ${isActive ? 'text-white' : 'text-gray-700'}`}>{level.name}</p>
                            <p className={`text-xs ${isActive ? 'text-white/80' : 'text-gray-500'}`}>
                              {level.min}+ pts
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Stats Cards */}
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl flex items-center justify-center">
                          <Coins className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-[#1e3a5f]">{dashboardData?.stats?.coins || 0}</p>
                          <p className="text-gray-500">Moedas Virtuais</p>
                          <p className="text-xs text-gray-400">Acumule para futuras recompensas</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl flex items-center justify-center">
                          <Zap className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-[#1e3a5f]">
                            {(gamification?.assessments_completed || 0) + (gamification?.trainings_completed || 0)}
                          </p>
                          <p className="text-gray-500">Atividades Completadas</p>
                          <p className="text-xs text-gray-400">
                            {gamification?.assessments_completed || 0} avaliações • {gamification?.trainings_completed || 0} treinamentos
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Points History */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <History className="w-5 h-5 text-[#0d9488]" />
                      Histórico de Pontos
                    </CardTitle>
                    <Button variant="outline" size="sm" onClick={fetchPointsHistory}>
                      <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {pointsHistory.length > 0 ? (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {pointsHistory.map((entry, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                                <Star className="w-4 h-4 text-emerald-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{entry.reason}</p>
                                <p className="text-xs text-gray-500">
                                  {new Date(entry.awarded_at).toLocaleDateString('pt-BR')}
                                </p>
                              </div>
                            </div>
                            <Badge className="bg-emerald-500">+{entry.points} pts</Badge>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <History className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                        <p className="text-gray-500">Clique em "Atualizar" para ver seu histórico</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* How to Earn Points */}
                <Card>
                  <CardHeader>
                    <CardTitle>Como Ganhar Pontos</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-4">
                      {[
                        { action: 'Responder avaliação', points: 50, icon: ClipboardList },
                        { action: 'Completar treinamento', points: 100, icon: GraduationCap },
                        { action: 'Nota perfeita no quiz', points: 50, icon: Trophy },
                        { action: 'Primeira avaliação', points: 25, icon: Star },
                      ].map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <item.icon className="w-5 h-5 text-[#0d9488]" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.action}</p>
                          </div>
                          <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                            +{item.points} pts
                          </Badge>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-4 text-center">
                      A cada 10 pontos você ganha 1 moeda virtual
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Assessments Tab */}
            {activeTab === 'assessments' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-[#1e3a5f]">Minhas Avaliações</h2>
                
                {dashboardData?.pending_assessments?.length > 0 ? (
                  <div className="grid gap-4">
                    {dashboardData.pending_assessments.map(assessment => (
                      <Card key={assessment.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg">{assessment.titulo}</h3>
                              {assessment.descricao && (
                                <p className="text-gray-600 text-sm mt-1">{assessment.descricao}</p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                {assessment.prazo && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" /> Prazo: {assessment.prazo}
                                  </span>
                                )}
                                <Badge className="bg-amber-500">Pendente</Badge>
                                <Badge variant="outline" className="text-emerald-600 border-emerald-600">+50 pts</Badge>
                              </div>
                            </div>
                            <Button 
                              className="bg-[#0d9488] ml-4"
                              onClick={() => window.open(`/avaliar/${assessment.token}`, '_blank')}
                            >
                              Responder
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                      <p className="text-gray-600">Nenhuma avaliação pendente</p>
                      <p className="text-sm text-gray-400">Você está em dia!</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Trainings Tab */}
            {activeTab === 'trainings' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-[#1e3a5f]">Meus Treinamentos</h2>
                
                {dashboardData?.trainings?.length > 0 ? (
                  <div className="grid gap-4">
                    {dashboardData.trainings.map(training => {
                      const videoInfo = extractVideoUrl(training.descricao) || 
                                       (training.conteudo && typeof training.conteudo === 'string' && extractVideoUrl(training.conteudo));
                      
                      return (
                        <Card key={training.enrollment_id} className={`hover:shadow-md transition-shadow ${training.tipo === 'dds' ? 'border-l-4 border-l-amber-500' : ''}`}>
                          <CardContent className="p-4">
                            {/* Video Player no topo se houver vídeo */}
                            {videoInfo && <VideoPlayer videoInfo={videoInfo} />}
                            
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-semibold text-lg">{training.titulo}</h3>
                                  {getStatusBadge(training.status)}
                                  {training.tipo === 'dds' && (
                                    <Badge className="bg-amber-500">DDS</Badge>
                                  )}
                                  {videoInfo && (
                                    <Badge className="bg-red-500">
                                      <Video className="w-3 h-3 mr-1" />
                                      Vídeo
                                    </Badge>
                                  )}
                                </div>
                                {training.descricao && (
                                  <p className="text-gray-600 text-sm mt-1">
                                    {training.descricao.replace(/https?:\/\/[^\s]+/g, '').trim() || training.descricao}
                                  </p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                  <span>{training.carga_horaria}h de duração</span>
                                  <Badge variant="outline" className="text-emerald-600 border-emerald-600">
                                    +{training.pontos} pts
                                  </Badge>
                                </div>
                                {training.status === 'em_andamento' && (
                                  <div className="mt-2">
                                    <div className="flex justify-between text-xs mb-1">
                                      <span>Progresso</span>
                                      <span>{training.progresso}%</span>
                                    </div>
                                    <Progress value={training.progresso} className="h-2" />
                                  </div>
                                )}
                              </div>
                              {training.status !== 'concluido' && (
                                <Button className={`ml-4 ${training.tipo === 'dds' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-[#1e3a5f]'}`}>
                                  {training.status === 'nao_iniciado' ? 'Iniciar' : 'Continuar'}
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <GraduationCap className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-600">Nenhum treinamento atribuído</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Certificates Tab */}
            {activeTab === 'certificates' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-[#1e3a5f]">Meus Certificados</h2>
                
                {dashboardData?.certificates?.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {dashboardData.certificates.map(cert => (
                      <Card key={cert.enrollment_id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Award className="w-7 h-7 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold truncate">{cert.titulo}</h3>
                              <p className="text-sm text-gray-500">
                                Nota: {cert.nota}% • {cert.carga_horaria}h
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                Concluído em: {new Date(cert.data_conclusao).toLocaleDateString('pt-BR')}
                              </p>
                              {cert.data_validade && (
                                <p className="text-xs text-amber-600">
                                  Válido até: {new Date(cert.data_validade).toLocaleDateString('pt-BR')}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <Award className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-600">Nenhum certificado obtido ainda</p>
                      <p className="text-sm text-gray-400">Complete seus treinamentos para obter certificados</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default EmployeePortal;
